import React, { useRef, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDates } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { Utills } from '../../../../../../shared/app-configs/utilit';
import { dtrackItServiceV4 } from '../../../sc-service/dtrackit.service';
import { RestrictedDataValidation } from '../../../../../../shared/utils/custom-validation.utils';
import { useApi } from '../../../../../../shared/hooks/useApi';
import useNotification from '../../../../../../shared/hooks/useNotification';
import { TCAppConfig } from '../../../../../../shared/app-configs/tc-app-configs';
import { TroubleshootRounded } from '@mui/icons-material';

interface ScopeCertificateDatesFormProps {
  formRef: any;
  datesData: ScopeCertificateDates;
  setDatesData: React.Dispatch<React.SetStateAction<ScopeCertificateDates>>;
  scStatus?: string; // Add SC Status prop
  isAmendment?: boolean;
}

const ScopeCertificateDatesForm: React.FC<ScopeCertificateDatesFormProps> = ({
  formRef,
  datesData,
  setDatesData,
  scStatus,
  isAmendment,
}) => {
  console.log("isAmendment", isAmendment);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const { notify } = useNotification();
  const [accreditationBodyOptions, setAccreditationBodyOptions] = useState<Array<{value: string, label: string}>>([]);
  const { executeGet } = useApi();
  
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setDatesData(datesData);
  };



  // Get states from API with search and pagination
  const getStates = async (searchQuery: string, offset: number = 0) => {
    try {
      
      const queryParams: any = {
        query_param: 'ST',
        limit: 10,
        offset: offset
      };
      
      if (searchQuery) {
        queryParams.state_desc = searchQuery;
      }
      
      const result: any = await executeGet(
        TCAppConfig.GET_DTRACKIT_REF_CODE_V4,
        queryParams
      );
      
      
      if (result?.data && Array.isArray(result.data)) {
        // Return the raw data for SearchInput component
        return result.data;
      } else {
        return [];
      }
    } catch (err) {
      notify('Error in Fetching States', 'error');
      Utills.cl(`Error in Fetching States ${err}`);
      return [];
    }
  };

  // Get accreditation body options from API
  const getAccreditationBody = async () => {
    try {
      const result: any = await dtrackItServiceV4.getAccreditationBody();
      
      if (result?.data && Array.isArray(result.data)) {
        const accreditationBodies = result.data.map((item: any) => ({
          value: item.code_1,
          label: item.description_1,
        }));
        setAccreditationBodyOptions(accreditationBodies);
      } else {
        setAccreditationBodyOptions([]);
      }
    } catch (err) {
      Utills.cl(`Error in Fetching Accreditation Body ${err}`);
      setAccreditationBodyOptions([]);
    }
  };

  useEffect(() => {
    getAccreditationBody();
  }, []);

  // Update form when datesData changes
  useEffect(() => {
    if (formRef?.current) {
      formRef.current.setValues(datesData);
      
      // Handle scPlaceOfIssue parsing for consistent display and hydration
      if (datesData.scPlaceOfIssue) {
        const placeOfIssue = datesData.scPlaceOfIssue;
        let stateCode: string | undefined;
        let stateName: string | undefined;

        // Check if it's the code format (XX-XX or XX-XXX): "IN-AS", "AU-VIC", etc.
        if (/^[A-Z]{2}-[A-Z]{2,3}$/.test(placeOfIssue)) {
          // New format: just the code (e.g., "IN-AS" or "AU-VIC") - as per documentation
          stateCode = placeOfIssue;
        } else {
          // Legacy format: "Country (CC), StateName (CC-SC)" or "StateName (CC-SC)" - for backward compatibility
          const statePart = placeOfIssue.includes(',')
            ? placeOfIssue.split(', ')[1]
            : placeOfIssue;
          const nameAndCodeMatch = statePart.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
          stateName = nameAndCodeMatch ? nameAndCodeMatch[1] : statePart;
          stateCode = nameAndCodeMatch ? nameAndCodeMatch[2] : undefined;

          if (stateName) {
            formRef.current.setFieldValue('scStateOrProvince', stateName);
            formRef.current.setFieldValue('scStateOrProvinceDescription', stateName);
          }

          // Hydrate the search field object so submit can rebuild consistently
          if (stateCode) {
            const existingObject = (formRef.current as any)?.values?.scStateOrProvinceObject;
            if (!existingObject || existingObject.code_1 !== stateCode) {
              formRef.current.setFieldValue('scStateOrProvinceObject', {
                code_1: stateCode,
                description_1: stateName || stateCode,
              });
              formRef.current.setFieldValue('scPlaceOfIssue', stateCode);
            }
          }
        }
      }
      
      // Auto-update scValidFromDate based on business logic
      if (datesData.scCreatedDate) {
        const currentValidFromDate = datesData.scValidFromDate;
        
        // Auto-update when scValidFromDate is empty OR when created date is after current valid from date
        if (!currentValidFromDate || new Date(datesData.scCreatedDate) > new Date(currentValidFromDate)) {
          formRef.current.setFieldValue('scValidFromDate', datesData.scCreatedDate);
        }
      }
      
      // Auto-update scLastUpdatedDate with scCreatedDate in creation workflow (when not amendment)
      if (datesData.scCreatedDate && !isAmendment && !datesData.scLastUpdatedDate) {
        formRef.current.setFieldValue('scLastUpdatedDate', datesData.scCreatedDate);
      }
    }
  }, [datesData, formRef]);



  // Commented out hardcoded options - now using API calls
  // const accreditationBodyOptions = [
  //   { label: "SLAB", value: "SLAB" },
  //   { label: "IOAS", value: "IOAS" },
  //   { label: "ANAB", value: "ANAB" },
  //   { label: "TAF", value: "TAF" },
  //   { label: "COFRAC", value: "COFRAC" },
  //   { label: "IA Japan", value: "IAJapan" },
  //   { label: "DAkkS", value: "DAkkS" },
  // ];

  const formConfig = {
    pageName: "scope-certificate-details",
    initialValues: datesData,
    validationSchema: Yup.object().shape({
      scCreatedDate: Yup.date()
        .required('SC First Created Date is required'),
      
      scLastUpdatedDate: Yup.date()
        .required('SC Last Updated Date is required')
        .test('last-updated-sequence', 'Last Updated Date must be after or equal to First Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            return new Date(firstCreated) <= new Date(value);
          })
        .test('last-updated-amendment', 'During amendments, Last Updated Date must be after or equal to Created Date',
          function(value) {
            if (!isAmendment || !value) return true;
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated) return true;
            return new Date(firstCreated) <= new Date(value);
          }),
      
      scExtendedUntilDate: Yup.date()
        .nullable()
        .test('extended-until-sequence', 'Extended Until Date must be after or equal to Valid Until Date',
          function(value) {
            const validUntil = this.parent.scValidFromDate;
            if (!validUntil || !value) return true;
            return new Date(validUntil) <= new Date(value);
          }),
      
      scLastAuditStartDate: Yup.date()
        .required('SC Last Audit Start Date is required'),
      
      scValidFromDate: Yup.date()
        .required('SC Valid From Date is required')
        .test('valid-from-sequence', 'Valid From Date must be after or equal to First Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            return new Date(firstCreated) <= new Date(value);
          })
        .test('valid-from-max-days', 'SC Valid From Date cannot be more than 60 days after SC Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            const createdDate = new Date(firstCreated);
            const validFromDate = new Date(value);
            const daysDifference = Math.ceil((validFromDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            return daysDifference <= 60;
          }),
      
      scValidUntilDate: Yup.date()
        .required('SC Valid Until Date is required')
        .test('valid-until-sequence', 'Valid Until Date must be atleast 180 days after SC Valid From Date',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;
            const validFromDate = new Date(validFrom);
            const validUntilDate = new Date(value);
            const daysDifference = Utills.calculateDateDifferenceInDays(validFromDate,validUntilDate)
            return daysDifference >= 180 && daysDifference>=0;
          }),
      
      scWithdrawalDate: Yup.date()
        .when([], {
          is: () => scStatus === 'W',
          then: (schema) => schema.required('Withdrawal Date is required when SC Status is Withdrawn'),
          otherwise: (schema) => schema.nullable()
        })
        .test('withdrawal-sequence', 'Withdrawal Date must be after or equal to Valid From Date',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;
            return new Date(validFrom) <= new Date(value);
          }),
      
      scLastAuditDate: Yup.date()
        .required('SC Last Audit Date is required')
        .test('audit-date-sequence', 'Last Audit Date must be after or equal to Last Audit Start Date', 
          function(value) {
            const startDate = this.parent.scLastAuditStartDate;
            if (!startDate || !value) return true;
            return new Date(startDate) <= new Date(value);
          }),
      
      scStateOrProvince: Yup.string()
        .required('Select State or Province is required'),
      
      scCbAccreditationBody: Yup.string()
        .required('SC CB Accreditation Body is required'),
      
      scCbAccreditationBodyDescription: Yup.string(),
      
      scCbScopeStipulations: Yup.string()
        .required('SC CB Scope Stipulations is required')
        .test('na-lowercase-only', 'Only lowercase "na" allowed for NA value', (value) => {
          if (!value) return true;
          const lower = value.trim().toLowerCase();
          if (["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"].includes(lower)) {
            return lower === "na";
          }
          return true;
        }),
      
      scCbAccreditationNo: Yup.string()
        .nullable()
        .max(50, 'Max 50 characters'),

      scPlaceOfIssue: Yup.string()
        .required('SC Place of Issue is required')
        .max(6, 'SC Place of Issue must be at most 6 characters')
        .matches(/^[A-Z]{2}-[A-Z]{2,3}$/, 'Invalid format. Expected: XX-XX or XX-XXX (e.g., IN-AS, AU-VIC)'),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: 'Certificate Dates Details',
        fields: [
          {
            label: 'SC Created Date',
            name: 'scCreatedDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled:(values: any) => Boolean(isAmendment), // Disable during amendments
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                if (newValue && formRef?.current) {
                  const currentValidFromDate = updatedValues?.scValidFromDate;
                  
                  // Auto-update scValidFromDate only when it's empty OR when new created date is after current valid from date
                  if (!currentValidFromDate || new Date(newValue) > new Date(currentValidFromDate)) {
                    formRef.current.setFieldValue('scValidFromDate', newValue);
                  }
                  
                  // Auto-update scLastUpdatedDate with scCreatedDate in creation workflow (when not amendment)
                  if (!isAmendment && !updatedValues?.scLastUpdatedDate) {
                    formRef.current.setFieldValue('scLastUpdatedDate', newValue);
                  }
                }
              },
            },
            props: { 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years")
            },
          },
          {
            label: 'SC Last Updated Date',
            name: 'scLastUpdatedDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scCreatedDate ? moment(values.scCreatedDate) : undefined
            }),
          },
          {
            label: 'SC Extended Until Date',
            name: 'scExtendedUntilDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scValidUntilDate ? moment(values.scValidUntilDate) : undefined
            }),
          },
          {
            label: 'SC Last Audit Start Date',
            name: 'scLastAuditStartDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: { 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years")
            },
          },
          {
            label: 'SC Valid From Date',
            name: 'scValidFromDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: Boolean(isAmendment), // Disable during amendments
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: values?.scCreatedDate ? moment(values.scCreatedDate).clone().add(60, 'days') : moment().add(100, 'years'),
              minDate: values?.scCreatedDate ? moment(values.scCreatedDate) : undefined
            }),
          },
          {
            label: 'SC Valid Until Date',
            name: 'scValidUntilDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: Boolean(isAmendment), // Disable during amendments
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate) : undefined
            }),
          },
          {
            label: 'SC Withdrawal Date',
            name: 'scWithdrawalDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: scStatus === "W",
            disabled: (values: any) => scStatus !== "W",
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate) : undefined
            }),
          },
          {
            label: 'SC Last Audit Date',
            name: 'scLastAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scLastAuditStartDate ? moment(values.scLastAuditStartDate) : undefined
            }),
          },
           {
             label: 'SC CB Scope Stipulations',
             name: 'scCbScopeStipulations',
             type: 'text',
             gridSize: { xs: 12, md: 6, lg: 6 },
             required: true,
             helperText: 'Please fill "na" if SC CB Scope Stipulations is not applicable',
             props: { 
               placeholder: 'Enter SC CB Scope Stipulations'
             },
           },
        ],
      },
      {
        title: 'SC Place of Issue',
        fields: [
          {
            label: 'Select State or Province',
            name: 'scStateOrProvince',
            type: 'search',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            events: {
              onChange: (selectedState: any) => {
                if (selectedState) {
                  // Store the full object for later use in handleSubmit
                  formRef.current?.setFieldValue('scStateOrProvinceObject', selectedState);
                  formRef.current?.setFieldValue('scStateOrProvince', `${selectedState.description_1} (${selectedState.code_1})`);
                  formRef.current?.setFieldValue('scStateOrProvinceDescription', selectedState.description_1);
                  // Set scPlaceOfIssue with the code only (XX-XX or XX-XXX format, max 6 characters) as per documentation
                  formRef.current?.setFieldValue('scPlaceOfIssue', selectedState.code_1 || '');
                } else {
                  // Clear all related fields when state is cleared
                  formRef.current?.setFieldValue('scStateOrProvinceObject', null);
                  formRef.current?.setFieldValue('scStateOrProvince', '');
                  formRef.current?.setFieldValue('scStateOrProvinceDescription', '');
                  formRef.current?.setFieldValue('scPlaceOfIssue', '');
                }
              },
            },
            props: { 
              displayKey: 'description_1 code_1',
              placeholder: 'Search State or Province',
              param: 'ST',
              showCodeInDropdown: true,
              onSearch: async (searchQuery: string, offset: number) => {
                return await getStates(searchQuery, offset);
              },
            },
          },
        ],
      },
      {
        title: 'Accreditation Details',
        fields: [
         
          {
            label: 'SC CB Accreditation Body',
            name: 'scCbAccreditationBody',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                // Store the description for the selected accreditation body
                const selectedAccreditationBody = accreditationBodyOptions.find(body => body.value === newValue);
                if (selectedAccreditationBody) {
                  formRef.current?.setFieldValue('scCbAccreditationBodyDescription', selectedAccreditationBody.label);
                }
              },
            },
            props: { 
              options: accreditationBodyOptions,
              placeholder: 'Select SC CB Accreditation Body'
            },
          },
           {
            label: 'SC CB Accreditation Number',
            name: 'scCbAccreditationNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { 
              placeholder: 'Enter SC CB Accreditation Number'
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      // Extract only the code (XX-XX or XX-XXX format, max 6 characters) as per documentation
      // Documentation states: "the state/province code only for data submissions"
      // Pattern: XX-XXX (e.g., "AU-VIC", "IN-AS")
      // The full state object is stored in scStateOrProvinceObject
      const selectedState = values.scStateOrProvinceObject;
      const placeOfIssue = selectedState?.code_1 || values.scPlaceOfIssue || '';
      
      const updatedValues = {
        ...values,
        scPlaceOfIssue: placeOfIssue,
        scPlaceOfIssueDescription:values?.scStateOrProvinceDescription??selectedState?.description_1??""
      };
      
      setDatesData(updatedValues);
      toggleDrawer();
    },
  };

  return <FormBuilder 
    formConfig={formConfig} 
    onSubmit={formConfig.handleSubmit} 
    formRef={formRef} 
  />;
};

export default ScopeCertificateDatesForm;

